import { getJangjiRecommendRequests } from "@/apis/jangjiRecommend";
import BannerCTA from "@/components/common/BannerCTA";
import AuthProvider from "@/components/Provider/AuthProvider";
import { useDialog } from "@/hooks/useDialog";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
} from "@chakra-ui/react";
import {
  Button,
  call,
  ColorIcon,
  colors,
  customEvent,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  HStack,
  queryKeys,
  Spacing,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { 장지유형_LIST, 장지희망서비스_LIST } from "./constants/request";

export default function MatchRecommendationFacilityContainer() {
  const router = useRouter();
  const { onOpen, onClose } = useDialog();
  const hasPrevious = router.query.hasPrevious as string;
  useEffect(() => {
    if (hasPrevious) {
      onOpen({
        type: 1,
        closeOnOverlayClick: false,
        header: "이미 제출한 장지 견적서가 있어요",
        children: (
          <>
            작성해주신 견적서를 바탕으로 <br /> 상담이 진행중인 상태에요.
            <br /> 견적 내용을 수정하시려면,
            <br /> 카카오톡 채널로 문의주세요.
          </>
        ),
        footerProps: {
          text: "닫기",
          onClick: () => {
            customEvent({
              newGtm: {
                path: "facility_match_recommendation",
                trigger_and_target: "check_previous_result-uv_consult-list",
              },
            });
            onClose();
          },
        },
      });
    }
  }, [hasPrevious]);
  const { data, isLoading } = useQuery(
    queryKeys.jangjiRecommendRequest,
    getJangjiRecommendRequests,
    {
      select: (result) => {
        if (result[0]) {
          return result[0];
        } else return undefined;
      },
    },
  );

  const answers = useMemo(() => {
    if (data)
      return {
        desiredType: 장지유형_LIST?.find((item) => item.value === data.desired_type)?.text,
        factor: data.most_important_factor,
        addtionalService: 장지희망서비스_LIST?.find(
          (item) => item.value === data.additional_service,
        )?.text,
        origin: `${FUNERAL_SIDO.get(Number(data.origin_sido)) as string} ${
          FUNERAL_SIGUNGU.get(Number(data.origin_sido))?.get(Number(data.origin_sigungu)) as string
        }`,
        jangji: `${FUNERAL_SIDO.get(Number(data.jangji_sido)) as string} ${
          FUNERAL_SIGUNGU.get(Number(data.jangji_sido))?.get(Number(data.jangji_sigungu)) as string
        }`,
        memo: data.memo,
      };
  }, [data]);
  if (isLoading) {
    return null;
  }
  return (
    <AuthProvider redirectToIfNotLoggedIn="/match/request/">
      <VStack alignItems="center">
        <VStack id="wrapper" width="100%" maxWidth="480px">
          <BannerCTA
            variant="GREEN"
            subtitle="영정사진 준비, 심리 상담, 프리미엄 서비스까지"
            title="미리 준비하고 20만원 할인 혜택 받기"
            event={{
              path: "mtch_recomm_dtl",
              trigger_and_target: "banner_CTA-uv_p_p",
            }}
            href="/benefit/prepare-package/"
          />
          <VStack>
            <Box padding="20px 24px 12px" className="head2_b">
              장지 추천(맞춤형)
            </Box>
            <Box padding="0 16px">
              <HStack
                as="button"
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "facility_match_recommendation",
                      trigger_and_target: "recommend-uv_match_request",
                    },
                  });
                  router.push("/match/request/");
                }}
                width="100%"
                borderRadius="5px"
                background={colors.orange50}
                padding="16px 20px 16px 10px"
                alignItems="center"
                justifyContent="space-between"
              >
                <div className="caption_12_b orange650 font-left">
                  상조 서비스, 장례식장도 추천해드려요
                  <br />
                  <span className="caption_12_b gray500">
                    고이에서 까다롭게 선별한 장례지도사 추천받기
                  </span>
                </div>
                <ColorIcon name="arrow" color={colors.gray500} width="16px" height="16px" />
              </HStack>
            </Box>
            <Spacing gap={8} />

            <Accordion allowToggle allowMultiple defaultIndex={[0]}>
              <AccordionItem>
                <AccordionButton
                  display="flex"
                  justifyContent="space-between"
                  padding="16px 24px"
                  _expanded={{
                    background: "white",
                  }}
                  _hover={{
                    background: "white",
                  }}
                >
                  <Box
                    as="h3"
                    className="subtitle_14_b gray900"
                    paddingRight="24px"
                    wordBreak="keep-all"
                    textAlign="left"
                  >
                    장지 견적서
                    <br />
                    <span className="caption_12_b gray500">고객님이 남겨주신 답변이에요</span>
                  </Box>
                  <AccordionIcon width="24px" height="24px" color={colors.gray300} />
                </AccordionButton>
                {answers && (
                  <AccordionPanel padding="0">
                    <Box
                      as="p"
                      padding="24px"
                      background={colors.warmGray50}
                      className="body_14_r gray700"
                    >
                      <table style={{ width: "100%" }}>
                        <VStack as="tbody" gap="10px" alignItems="start">
                          <TableRow
                            title="희망 유형"
                            description={
                              answers.desiredType
                                ? answers.desiredType
                                : answers.memo.replace("[개별 장지 견적 제출]: ", "")
                            }
                          />
                          <TableRow title="선호하는 요인" description={answers.factor} />
                          <TableRow title="희망 서비스" description={answers.addtionalService} />
                          <TableRow title="지역(현재)" description={answers.origin} />
                          <TableRow title="장지 희망지역" description={answers.jangji} />
                        </VStack>
                      </table>
                    </Box>
                  </AccordionPanel>
                )}
              </AccordionItem>
            </Accordion>
            <VStack padding="40px">
              <Box className="subtitle_18_b font-center">
                <span className="orange650">{answers?.jangji}</span> 장지는
                <br />
                전문 상담이 필요해요
              </Box>
              <Spacing gap={16} />
              <Box className="body_14_r font-center">
                현재 {answers?.jangji} 장지는
                <br />
                직접 전문 상담을 진행해드리고 있어요.
                <br />
                <br />
                작성해주신 응답을 바탕으로
                <br />
                전화를 드릴 예정이에요.
              </Box>
              <Spacing gap={24} />

              <Button
                className="button_14_sb white"
                css={{
                  margin: "auto",
                  width: "fit-content",
                  backgroundColor: colors.orange650,
                  padding: "10px 12px",
                  borderRadius: "5px",
                }}
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "facility_match_recommendation",
                      trigger_and_target: "quickcall",
                    },
                  });
                  call("1666-9784");
                }}
              >
                지금 바로 문의하기
              </Button>
            </VStack>
          </VStack>
        </VStack>
      </VStack>
    </AuthProvider>
  );
}

const TableRow = ({ title, description }: { title: string; description?: string }) => {
  return (
    <HStack as="tr">
      <Box as="td" className="subtitle_14_sb gray600" minWidth="110px">
        {title}
      </Box>
      <Box as="td" className="body_14_m gray900">
        {description}
      </Box>
    </HStack>
  );
};
